import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu, X, ChevronDown, ChevronRight, LogOut, Search, ShoppingCart,
} from 'lucide-react'
import { cn } from '../../app/libs/cn'
import { useAuthStore } from '@/store/authStore'
import { useFavoriteStore } from '@/store/favoriteStore'
import { useCart } from '@/services/cartService'
import { logout as logoutApi } from '@/services/authService'
import { getProfile } from '@/services/profileService'
import { useProfileStore } from '@/store/profileStore'
import { getCourseCategories } from '@/services/courseService'
import { getJobCategories, type ApiJobCategory } from '@/services/jobService'
import type { ApiCourseCategory } from '@/types/course'
import logoMark from '@/assets/logo-mark-v3.png'
import logoWordmark from '@/assets/logo-wordmark.png'
import businessLogo from '@/assets/logo-business.png'
import { useBookCategories } from '@/services/bookService'

// หัวข้อหลักในเมนูมือถือ (คอร์สเรียน/อบรม/หนังสือ/หางาน) — ทำเป็นแถบมีพื้นหลัง + ลูกศร ให้เห็นชัดว่ากดได้ และแยกจากรายการหมวดย่อย
const MOBILE_HEADING =
  'mt-2 flex items-center justify-between rounded-lg border border-accent/15 bg-accent/5 px-3 py-3 text-[15px] font-semibold text-text transition-colors hover:bg-accent/10'

const navLinks = [
  { label: 'หน้าแรก', href: '/' },
]

// จัดกลุ่มเดียวกับ sidebar ของหน้า /account (ดู COMPANY_TAB_GROUPS/INDIVIDUAL_TAB_GROUPS ใน
// AccountShell.tsx) เพื่อให้ dropdown นี้ตรงกับ tab ที่มีจริง — ไม่ใส่รายการที่ไม่มี backend รองรับ
// (เช่น หลักสูตรปริญญา/แต้มสะสม) และไม่ใส่ tab ที่บัญชีประเภทนั้นไม่มีสิทธิ์เข้าถึง
const INDIVIDUAL_MENU_GROUPS = [
  {
    items: [
      { label: 'คอร์สเรียนของฉัน', href: '/account?tab=courses' },
      { label: 'สกิลของฉัน', href: '/account?tab=skills' },
      { label: 'ประกาศนียบัตร', href: '/account?tab=certificates' },
      { label: 'คอร์สที่ถูกใจ', href: '/account?tab=wishlist' },
    ],
  },
  {
    items: [
      { label: 'ใบสมัครงานของฉัน', href: '/account?tab=my-applications' },
    ],
  },
  {
    items: [
      { label: 'โปรไฟล์ของฉัน', href: '/account?tab=profile' },
      { label: 'ตะกร้าสินค้า', href: '/cart' },
      { label: 'ประวัติคำสั่งซื้อ', href: '/account?tab=orders' },
      { label: 'คำขอ In-house', href: '/account?tab=inhouse' },
    ],
  },
]

// บัญชีบริษัท — เมนูต่างจากบุคคลทั่วไป (ไม่มีคอร์ส/ใบประกาศนียบัตร/คอร์สที่ถูกใจส่วนตัว)
const COMPANY_MENU_GROUPS = [
  {
    items: [
      { label: 'ประกาศงานของบริษัท', href: '/account?tab=job-postings' },
    ],
  },
  {
    items: [
      { label: 'โปรไฟล์บริษัท', href: '/account?tab=profile' },
      { label: 'ตะกร้าสินค้า', href: '/cart' },
      { label: 'ประวัติคำสั่งซื้อ', href: '/account?tab=orders' },
      { label: 'คำขอ In-house', href: '/account?tab=inhouse' },
    ],
  },
]

// วงกลมอวาตาร์: มีรูปโปรไฟล์/โลโก้ก็โชว์รูป ถ้าไม่มีหรือรูปโหลดไม่ขึ้นให้ถอยกลับเป็นตัวอักษรแรก
function AvatarContent({ imageUrl, initial }: { imageUrl: string | null; initial: string }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [imageUrl])
  if (imageUrl && !failed) {
    return <img src={imageUrl} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
  }
  return <>{initial}</>
}

export default function Navbar() {
  const bookCategories = useBookCategories()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [mobileSearch, setMobileSearch] = useState('')

  const isLoggedIn = useAuthStore((s) => s.isLoggedIn)
  const customerType = useAuthStore((s) => s.customerType)
  const cartCount = useCart().count
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const displayLabel = customerType === 'COMPANY' ? 'บริษัทของฉัน' : 'บัญชีของฉัน'
  const userMenuGroups = customerType === 'COMPANY' ? COMPANY_MENU_GROUPS : INDIVIDUAL_MENU_GROUPS

  // ชื่อ/อีเมล/รูปมาจาก store กลาง — หน้า "โปรไฟล์ของฉัน" อัปเดตให้ทันทีเมื่อบันทึก ไม่ต้องรีเฟรชหน้า
  const profileSummary = useProfileStore((s) => s.summary)
  const setProfileFromApi = useProfileStore((s) => s.setFromProfile)
  const clearProfileSummary = useProfileStore((s) => s.clear)
  const profileName = profileSummary?.name ?? null
  const profileEmail = profileSummary?.email ?? null
  const profileImage = profileSummary?.imageUrl ?? null

  // Real course/job categories, fetched from the actual backend — not hardcoded/mock,
  // so the dropdowns always match what's really in the database.
  const [courseCategories, setCourseCategories] = useState<ApiCourseCategory[]>([])
  const [jobCategories, setJobCategories] = useState<ApiJobCategory[]>([])
  useEffect(() => {
    getCourseCategories().then(setCourseCategories).catch(() => {})
    getJobCategories().then(setJobCategories).catch(() => {})
  }, [])

  useEffect(() => {
    if (!isLoggedIn) {
      // logout แล้วต้องล้างชื่อ/อีเมลของบัญชีเดิมทิ้งทันที ไม่งั้นถ้ามีบัญชีอื่น login ต่อในหน้าเดิม
      // (เช่นตอนกำลังจะ redirect) จะเห็นชื่อ/อีเมลของบัญชีก่อนหน้าค้างอยู่แวบหนึ่งก่อน getProfile() ใหม่จะเสร็จ
      Promise.resolve().then(() => clearProfileSummary())
      return
    }
    getProfile()
      .then(setProfileFromApi)
      .catch(() => {
        // เงียบไว้ — dropdown จะ fallback ไปโชว์ displayLabel แทน
      })
  }, [isLoggedIn, setProfileFromApi, clearProfileSummary])

  // ลบปุ่ม "สำหรับผู้ประกอบการ" ออกแล้ว (2026-10-02) — หน้า /employers ยังเข้าได้ทาง URL ตรง
  const visibleNavLinks = navLinks

  const fetchFavorites = useFavoriteStore((s) => s.fetchFavorites)
  const clearFavorites = useFavoriteStore((s) => s.clear)

  const handleLogout = async () => {
    if (refreshToken) {
      try {
        await logoutApi(refreshToken)
      } catch {
        // ignore — เคลียร์ token ฝั่ง client ต่อได้แม้ backend เรียกไม่สำเร็จ
      }
    }
    // บั๊กที่เจอ: เดิม logout แล้วไม่ navigate ไปไหนเลย ทำให้ path ปัจจุบัน (เช่น /account?tab=courses
    // หรือ /jobs/11/apply) ค้างอยู่ใน browser history — ถ้าหน้านั้นเป็น ProtectedRoute มันจะเด้งไป
    // /login พร้อม state.from = path เดิมของบัญชีที่เพิ่งออกไป แล้วถ้ามีคนอื่น (บัญชี B) มา login
    // ต่อที่ /login เดียวกันนี้ ระบบจะพาไป path เดิมของบัญชี A ทันที ซึ่งอาจไม่ตรงกับสิทธิ์ของ B เลย
    //
    // ⚠️ ลองแก้ด้วย navigate('/', {replace:true}) ก่อน clearAuth() มาแล้ว (ให้ React Router
    // เปลี่ยน path ก่อนที่ ProtectedRoute จะเห็น isLoggedIn=false) แต่ยังไม่พอ — เทสจริงแล้วยังเจอ
    // เด้งไป /login เหมือนเดิม เพราะ Zustand (useSyncExternalStore) sync การแจ้ง subscriber ของ
    // clearAuth() แบบ synchronous ทำให้ ProtectedRoute re-render เห็น isLoggedIn=false ก่อนที่
    // การเปลี่ยน path จาก navigate() (ซึ่งเป็น React state ปกติ ต้องรอ commit) จะมีผลจริง —
    // ลำดับ code ไม่ช่วยเพราะเป็นคนละกลไกการอัปเดต battling กันอยู่
    // แก้แบบชัวร์ที่สุด: ใช้ hard redirect (window.location.href) แทน SPA navigate — โหลดหน้าใหม่
    // ทั้งหมดที่ '/' หลังเคลียร์ auth เรียบร้อยแล้ว ตัดปัญหา race ระหว่าง React state 2 ระบบไปเลย
    clearAuth()
    clearFavorites()
    window.location.href = '/'
  }

  useEffect(() => {
    if (isLoggedIn) fetchFavorites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false) }, [pathname])

  // Close user menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <header className="fixed top-0 left-0 right-0 z-40 border-b border-black/10 bg-white">
      <nav className="relative mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          {/* บัญชีบริษัทใช้โลโก้ "For Business" เฉพาะที่ Navbar — ที่อื่นใช้โลโก้เดิม */}
          {isLoggedIn && customerType === 'COMPANY'
            ? <img src={businessLogo} alt="Jos CoWork For Business" className="h-14 w-auto object-contain" />
            : (
              <>
                <img src={logoMark} alt="" aria-hidden="true" className="h-9 w-auto object-contain" />
                <img src={logoWordmark} alt="JosCoWork" className="h-[18px] w-auto object-contain" />
              </>
            )}
        </Link>

        {/* Desktop Nav */}
        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 lg:flex">
          <Link
            to="/"
            className={cn(
              'rounded-lg px-3 py-2 text-sm transition-colors',
              pathname === '/' ? 'text-text font-medium' : 'text-text-muted hover:text-text'
            )}
          >
            หน้าแรก
          </Link>

          {/* ── Courses — real categories from GET /course-categories ── */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen('Courses')}
            onMouseLeave={() => setDropdownOpen(null)}
          >
            <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-text-muted hover:text-text transition-colors">
              คอร์สเรียน
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', dropdownOpen === 'Courses' && 'rotate-180')} />
            </button>
            <AnimatePresence>
              {dropdownOpen === 'Courses' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full left-0 mt-1 w-60 border border-[#E7E5FF] bg-white shadow-[0_8px_24px_rgba(108,99,255,0.12)]"
                >
                  <Link
                    to="/courses"
                    className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                  >
                    คอร์สทั้งหมด
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Link>
                  {courseCategories.map((cat) => (
                    <Link
                      key={cat.category_id}
                      to={`/courses?cat=${cat.category_id}`}
                      className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                    >
                      {cat.name}
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── อบรม (หลักสูตร Onsite) — แยกจากคอร์สเรียน ใช้หมวดหมู่ชุดเดียวกัน แต่ลิงก์ไป /trainings ── */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen('Trainings')}
            onMouseLeave={() => setDropdownOpen(null)}
          >
            <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-text-muted hover:text-text transition-colors">
              อบรม
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', dropdownOpen === 'Trainings' && 'rotate-180')} />
            </button>
            <AnimatePresence>
              {dropdownOpen === 'Trainings' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full left-0 mt-1 w-60 border border-[#E7E5FF] bg-white shadow-[0_8px_24px_rgba(108,99,255,0.12)]"
                >
                  <Link
                    to="/trainings"
                    className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                  >
                    หลักสูตรอบรมทั้งหมด
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Link>
                  {courseCategories.map((cat) => (
                    <Link
                      key={cat.category_id}
                      to={`/trainings?cat=${cat.category_id}`}
                      className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                    >
                      {cat.name}
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Books — หมวดหมู่หนังสือ (จาก API) ── */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen('Books')}
            onMouseLeave={() => setDropdownOpen(null)}
          >
            <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-text-muted hover:text-text transition-colors">
              หนังสือ
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', dropdownOpen === 'Books' && 'rotate-180')} />
            </button>
            <AnimatePresence>
              {dropdownOpen === 'Books' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full left-0 mt-1 w-60 border border-[#E7E5FF] bg-white shadow-[0_8px_24px_rgba(108,99,255,0.12)]"
                >
                  <Link
                    to="/books"
                    className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                  >
                    หนังสือทั้งหมด
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Link>
                  {bookCategories.map((cat) => (
                    <Link
                      key={cat.category_id}
                      to={`/books?cat=${cat.category_id}`}
                      className="flex items-center justify-between border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                    >
                      {cat.name}
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Jobs — real categories derived from the actual job listings data ── */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen('Jobs')}
            onMouseLeave={() => setDropdownOpen(null)}
          >
            <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-text-muted hover:text-text transition-colors">
              หางาน
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', dropdownOpen === 'Jobs' && 'rotate-180')} />
            </button>
            <AnimatePresence>
              {dropdownOpen === 'Jobs' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full left-0 mt-1 w-[30rem] border border-[#E7E5FF] bg-white shadow-[0_8px_24px_rgba(108,99,255,0.12)]"
                >
                  <Link
                    to="/jobs"
                    className="flex items-center justify-between border-b border-[#E7E5FF] px-4 py-3 text-sm font-medium text-[#19172A] transition-colors hover:bg-[#F3F1FF] hover:text-accent"
                  >
                    หางานทั้งหมด
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Link>
                  <div className="grid max-h-[60vh] grid-cols-2 gap-x-2 overflow-y-auto p-2">
                    {jobCategories.map((cat) => (
                      <Link
                        key={cat.job_category_id}
                        to={`/jobs?cat=${cat.job_category_id}`}
                        className="flex items-center justify-between gap-1 border-l-2 border-l-transparent px-3 py-2.5 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                      >
                        <span className="truncate">{cat.name}</span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {visibleNavLinks
            .filter((link) => link.href !== '/')
            .map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  'rounded-lg px-3 py-2 text-sm transition-colors',
                  (link as { highlight?: boolean }).highlight
                    ? 'rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 font-medium'
                    : pathname === link.href ? 'text-text font-medium' : 'text-text-muted hover:text-text'
                )}
              >
                {link.label}
              </Link>
            ))}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {isLoggedIn ? (
            <>
              {/* ปุ่มลัด → กระดิ่งแจ้งเตือน → โปรไฟล์วงกลม — บุคคลทั่วไป = "คอร์สเรียนของฉัน", บริษัท = "การโพสต์งาน" (ประกาศงานของบริษัท) */}
              <Link
                to={customerType === 'COMPANY' ? '/account?tab=job-postings' : '/account?tab=courses'}
                className="hidden items-center gap-1.5 rounded-xl border border-accent/30 bg-accent/10 px-4 py-2 text-sm font-medium text-accent transition-all hover:bg-accent/20 lg:flex"
              >
                {customerType === 'COMPANY' ? 'ประกาศงานของบริษัท' : 'คอร์สเรียนของฉัน'}
              </Link>

              <Link to="/cart" aria-label="ตะกร้าสินค้า" className="relative hidden rounded-full p-2 text-text-muted transition-colors hover:bg-surface2 hover:text-text lg:flex">
                <ShoppingCart className="h-4.5 w-4.5" />
                {cartCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>

              {/* ── User Avatar Dropdown ── */}
              <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="hidden lg:flex items-center gap-1.5"
              >
                <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-accent to-accent2 text-sm font-bold text-white">
                  <AvatarContent imageUrl={profileImage} initial={(profileName || displayLabel)[0]} />
                </div>
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform text-text-muted', userMenuOpen && 'rotate-180')} />
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute top-full right-0 mt-1 w-64 border border-[#E7E5FF] bg-white shadow-[0_8px_24px_rgba(108,99,255,0.12)] overflow-hidden"
                  >
                    {/* Compact profile header */}
                    <div className="flex items-center gap-3 border-b border-[#E7E5FF] px-4 py-3.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent/10 text-sm font-semibold text-accent">
                        <AvatarContent imageUrl={profileImage} initial={(profileName || displayLabel)[0]} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-[#19172A]">{profileName || displayLabel}</p>
                        {profileEmail && <p className="truncate text-xs text-[#69738A]">{profileEmail}</p>}
                      </div>
                    </div>

                    {/* Menu groups — same rectangular row style as the Courses/Jobs dropdowns */}
                    {userMenuGroups.map((group, gi) => (
                      <div key={gi} className={gi > 0 ? 'border-t border-[#E7E5FF]' : ''}>
                        {group.items.map((item) => (
                          <Link
                            key={item.label}
                            to={item.href}
                            className="block border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    ))}

                    {/* Logout */}
                    <div className="border-t border-[#E7E5FF]">
                      <button
                        onClick={() => { handleLogout(); setUserMenuOpen(false) }}
                        className="flex w-full items-center gap-2.5 border-l-2 border-l-transparent px-4 py-3 text-sm text-[#19172A] transition-colors hover:border-l-accent hover:bg-[#F3F1FF] hover:text-accent"
                      >
                        <LogOut className="h-4 w-4 shrink-0 text-[#69738A]" />
                        ออกจากระบบ
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>
            </>
          ) : (
            <>
              {/* ทางเข้าสำหรับบริษัทที่ยังไม่ล็อกอิน — /employers แสดงเหมือนหน้าแรกที่บัญชีบริษัทเห็น */}
              <Link
                to="/employers"
                className="hidden items-center gap-1.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-500/20 transition-all lg:flex"
              >
                สำหรับผู้ประกอบการ
              </Link>

              <Link
                to="/login"
                className="hidden items-center rounded-xl px-3 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors lg:flex"
              >
                เข้าสู่ระบบ
              </Link>

              <Link
                to="/register"
                className="hidden rounded-xl bg-gradient-to-r from-accent to-accent2 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-accent/25 hover:shadow-accent/40 hover:scale-105 transition-all lg:flex"
              >
                สมัครสมาชิก
              </Link>
            </>
          )}

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-text-muted hover:text-text transition-colors lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-b border-border bg-bg/95 backdrop-blur-xl lg:hidden"
          >
            {/* ลิงก์ที่ path เดิมแต่ query ต่างกัน (เช่น /courses → /courses?cat=4) ไม่ทำให้ pathname เปลี่ยน
                 effect ด้านบนจึงไม่ปิดเมนู/เลื่อนขึ้นบน — จับการกดลิงก์ตรงนี้แทน */}
            <div
              className="mx-auto max-w-7xl space-y-1 px-4 pb-6 pt-2"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) {
                  setMobileOpen(false)
                  window.scrollTo(0, 0)
                }
              }}
            >
              {/* Mobile search */}
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5">
                <Search className="h-4 w-4 shrink-0 text-text-muted" />
                <input
                  type="text"
                  value={mobileSearch}
                  onChange={(e) => setMobileSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && mobileSearch.trim()) {
                      navigate(`/courses?search=${encodeURIComponent(mobileSearch.trim())}`)
                      setMobileOpen(false)
                    }
                  }}
                  placeholder="ค้นหาคอร์สเรียน..."
                  className="w-full bg-transparent text-sm text-text placeholder:text-text-muted outline-none"
                />
              </div>

              {isLoggedIn && (
                <div className="mb-3 rounded-xl border border-border bg-surface p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-accent to-accent2 text-sm font-bold text-white">
                      <AvatarContent imageUrl={profileImage} initial={(profileName || displayLabel)[0]} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text">{profileName || displayLabel}</p>
                      {profileEmail && <p className="truncate text-xs text-text-muted">{profileEmail}</p>}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {userMenuGroups.flatMap((g) => g.items).map((item) => (
                      <Link key={item.label} to={item.href}
                        className="flex items-center rounded-lg px-2 py-1.5 text-xs text-text-muted hover:bg-surface2 hover:text-text transition-colors">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Courses — real categories, same source as desktop dropdown */}
              <div>
                <Link to="/courses" className={MOBILE_HEADING}>
                  <span>คอร์สเรียน</span>
                  <ChevronRight className="h-4 w-4 text-accent/70" />
                </Link>
                {courseCategories.map((cat) => (
                  <Link
                    key={cat.category_id}
                    to={`/courses?cat=${cat.category_id}`}
                    className="block rounded-lg py-2 pl-8 pr-3 text-sm text-text-muted hover:text-text transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* อบรม (หลักสูตร Onsite) */}
              <div>
                <Link to="/trainings" className={MOBILE_HEADING}>
                  <span>อบรม</span>
                  <ChevronRight className="h-4 w-4 text-accent/70" />
                </Link>
                {courseCategories.map((cat) => (
                  <Link
                    key={cat.category_id}
                    to={`/trainings?cat=${cat.category_id}`}
                    className="block rounded-lg py-2 pl-8 pr-3 text-sm text-text-muted hover:text-text transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Books — หมวดหมู่หนังสือ (MOCK) */}
              <div>
                <Link to="/books" className={MOBILE_HEADING}>
                  <span>หนังสือ</span>
                  <ChevronRight className="h-4 w-4 text-accent/70" />
                </Link>
                {bookCategories.map((cat) => (
                  <Link
                    key={cat.category_id}
                    to={`/books?cat=${cat.category_id}`}
                    className="block rounded-lg py-2 pl-8 pr-3 text-sm text-text-muted hover:text-text transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Jobs — real categories derived from the actual job listings data */}
              <div>
                <Link to="/jobs" className={MOBILE_HEADING}>
                  <span>หางาน</span>
                  <ChevronRight className="h-4 w-4 text-accent/70" />
                </Link>
                {jobCategories.map((cat) => (
                  <Link
                    key={cat.job_category_id}
                    to={`/jobs?cat=${cat.job_category_id}`}
                    className="block rounded-lg py-2 pl-8 pr-3 text-sm text-text-muted hover:text-text transition-colors"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {!isLoggedIn && (
                <Link to="/employers" className={MOBILE_HEADING}>
                  <span>สำหรับผู้ประกอบการ</span>
                  <ChevronRight className="h-4 w-4 text-accent/70" />
                </Link>
              )}


              <div className="mt-4 flex flex-col gap-2 pt-4 border-t border-border">
                {isLoggedIn ? (
                  <button
                    onClick={handleLogout}
                    className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-center text-sm font-medium text-rose-500"
                  >
                    ออกจากระบบ
                  </button>
                ) : (
                  <>
                    <Link to="/login" className="rounded-xl border border-accent/40 bg-accent/10 px-4 py-2.5 text-center text-sm font-medium text-accent">
                      เข้าสู่ระบบ
                    </Link>
                    <Link to="/register" className="rounded-xl bg-gradient-to-r from-accent to-accent2 px-4 py-2.5 text-center text-sm font-semibold text-white">
                      สมัครสมาชิก
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}